import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Save, MessageCircleQuestion, Lightbulb, ChevronDown, Download } from "lucide-react";

function scoreColor(s) {
    if (!s) return "text-foreground border-border";
    if (s >= 8) return "text-emerald-700 border-emerald-300 bg-emerald-50";
    if (s >= 5) return "text-amber-700 border-amber-300 bg-amber-50";
    return "text-rose-700 border-rose-300 bg-rose-50";
}


/**
 * QaExportPdfControls — Export button with a small popover that lets the
 * recruiter also email the PDF to interviewers assigned to the interview.
 *
 * Two-step UX:
 *   Click the "Export PDF" button → the popover reveals a checkbox for
 *   "Also email to interviewers" and an optional note field. If unchecked,
 *   we just download the file. If checked, we download AND fire
 *   ``POST /applications/{aid}/qa/pdf/email`` in parallel.
 */
function QaExportPdfControls({ applicationId }) {
    const [open, setOpen] = useState(false);
    const [emailAlso, setEmailAlso] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const downloadPdf = async () => {
        const res = await api.get(`/applications/${applicationId}/qa/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        const cd = res.headers?.["content-disposition"] || "";
        const match = cd.match(/filename="([^"]+)"/);
        a.download = match ? match[1] : `interview_qa_${applicationId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const submit = async () => {
        setBusy(true);
        try {
            const tasks = [downloadPdf()];
            if (emailAlso) {
                tasks.push(api.post(`/applications/${applicationId}/qa/pdf/email`, {
                    message: note.trim() || null,
                }));
            }
            const results = await Promise.allSettled(tasks);
            const dlOk = results[0].status === "fulfilled";
            if (!dlOk) throw results[0].reason;
            if (emailAlso) {
                const emailRes = results[1];
                if (emailRes.status === "fulfilled") {
                    const sent = emailRes.value?.data?.sent || 0;
                    if (sent > 0) {
                        toast.success(`PDF downloaded · Emailed to ${sent} interviewer${sent === 1 ? "" : "s"}`);
                    } else {
                        toast.warning("PDF downloaded · No interviewer emails were reachable");
                    }
                } else {
                    toast.warning(`PDF downloaded · Email failed: ${formatApiError(emailRes.reason)}`);
                }
            } else {
                toast.success("Interview Q&A PDF downloaded");
            }
            setOpen(false);
            setEmailAlso(false);
            setNote("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb inline-flex items-center gap-1"
                data-testid="qa-export-pdf-btn"
                title="Download this interview record as a PDF"
            >
                <Download className="w-3 h-3" /> Export PDF
            </button>
            {open && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => !busy && setOpen(false)}
                    />
                    <div
                        className="absolute right-0 top-full mt-1 z-50 w-72 border border-border bg-white shadow-lg p-3 space-y-3"
                        data-testid="qa-export-popover"
                    >
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Export options</div>
                        <label className="flex items-start gap-2 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                checked={emailAlso}
                                onChange={(e) => setEmailAlso(e.target.checked)}
                                className="mt-0.5 accent-ikb"
                                disabled={busy}
                                data-testid="qa-export-email-toggle"
                            />
                            <span>
                                Also email PDF to interviewers assigned to this application.
                            </span>
                        </label>
                        {emailAlso && (
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 block mb-1">
                                    Optional note
                                </label>
                                <Textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Anything you want the interviewers to see when they open this email…"
                                    rows={3}
                                    className="text-xs"
                                    disabled={busy}
                                    data-testid="qa-export-email-note"
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                disabled={busy}
                                className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb disabled:opacity-40"
                                data-testid="qa-export-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={busy}
                                className="ml-auto h-7 px-3 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-[#001f7a] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                                data-testid="qa-export-submit"
                            >
                                <Download className="w-3 h-3" />
                                {busy ? "Working…" : (emailAlso ? "Download & Email" : "Download PDF")}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function QARow({ item, onSave, onDelete, onAddFollowup, busy, applicationId }) {
    const [question, setQuestion] = useState(item.question || "");
    const [answer, setAnswer] = useState(item.answer || "");
    const [score, setScore] = useState(item.score || "");
    const [dirty, setDirty] = useState(false);
    const timerRef = useRef(null);

    // Sync local state if upstream item changes (e.g. after refresh)
    useEffect(() => {
        setQuestion(item.question || "");
        setAnswer(item.answer || "");
        setScore(item.score || "");
        setDirty(false);
    }, [item.id]); // eslint-disable-line

    const mark = (next) => {
        next();
        setDirty(true);
        if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Autosave on idle 1.2s
    useEffect(() => {
        if (!dirty) return;
        timerRef.current = setTimeout(() => {
            onSave(item.id, { question, answer, score: score ? Number(score) : null })
                .then(() => setDirty(false))
                .catch(() => {});
        }, 1200);
        return () => clearTimeout(timerRef.current);
    }, [question, answer, score, dirty]); // eslint-disable-line

    const isAi = item.source === "ai";
    const [followups, setFollowups] = useState(null);
    const [generating, setGenerating] = useState(false);

    const generateFollowups = async () => {
        setGenerating(true);
        try {
            const { api: apiClient } = await import("@/lib/api");
            const { data } = await apiClient.post(`/applications/${applicationId}/qa/${item.id}/follow-ups`);
            setFollowups(data?.followups || []);
        } catch (err) {
            setFollowups([]);
        } finally { setGenerating(false); }
    };

    return (
        <div className={`border ${isAi ? "border-blue-200 bg-blue-50/30" : "border-border bg-white"} p-4`} data-testid={`qa-row-${item.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span
                        className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border shrink-0 ${
                            isAi ? "border-blue-300 text-ikb bg-white" : "border-border text-muted-foreground bg-secondary"
                        }`}
                    >
                        {isAi ? <><Sparkles className="w-3 h-3" /> AI</> : "Custom"}
                    </span>
                    {!isAi ? (
                        <Input
                            value={question}
                            onChange={(e) => mark(() => setQuestion(e.target.value))}
                            placeholder="Question..."
                            className="rounded-none h-9 text-sm font-medium"
                            data-testid={`qa-question-${item.id}`}
                        />
                    ) : (
                        <div className="text-sm font-medium leading-snug" data-testid={`qa-question-${item.id}`}>{question}</div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <select
                        value={score || ""}
                        onChange={(e) => mark(() => setScore(e.target.value))}
                        className={`text-[10px] font-mono uppercase tracking-widest h-7 px-1.5 border bg-white ${scoreColor(Number(score))}`}
                        data-testid={`qa-score-${item.id}`}
                    >
                        <option value="">Rate</option>
                        {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{`${n}/10`}</option>)}
                    </select>
                    <button
                        type="button"
                        onClick={() => onDelete(item.id)}
                        disabled={busy}
                        className="text-muted-foreground hover:text-destructive p-1"
                        data-testid={`qa-delete-${item.id}`}
                        title={isAi ? "Remove this AI suggestion" : "Delete custom question"}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            <Textarea
                value={answer}
                onChange={(e) => mark(() => setAnswer(e.target.value))}
                placeholder="Candidate's answer — typed here, autosaves as you go..."
                rows={3}
                className="rounded-none text-sm resize-y"
                data-testid={`qa-answer-${item.id}`}
            />
            <div className="flex items-center justify-between gap-3 mt-1 flex-wrap">
                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 h-3">
                    {dirty ? "Saving..." : item.updated_at ? `Saved ${new Date(item.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                    {item.author?.name ? ` · added by ${item.author.name}` : ""}
                </div>
                <button
                    type="button"
                    onClick={generateFollowups}
                    disabled={generating || !answer.trim()}
                    className="text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 inline-flex items-center gap-1.5"
                    data-testid={`qa-followup-btn-${item.id}`}
                    title={answer.trim() ? "Generate sharper probes from this answer" : "Type the candidate's answer first"}
                >
                    <Lightbulb className="w-3 h-3" />
                    {generating ? "Thinking..." : "Generate follow-ups"}
                </button>
            </div>
            {followups && followups.length > 0 && (
                <div className="mt-3 border border-amber-200 bg-amber-50/40 p-2 space-y-1.5" data-testid={`qa-followups-${item.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" /> AI suggested follow-ups
                    </div>
                    {followups.map((f, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div className="flex-1 text-xs leading-snug">{f}</div>
                            <button
                                type="button"
                                onClick={() => onAddFollowup(f)}
                                className="text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 px-2 py-0.5 inline-flex items-center gap-1"
                                data-testid={`qa-followup-add-${item.id}-${i}`}
                            >
                                <Plus className="w-3 h-3" /> Add
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function InterviewQA({ applicationId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [newQ, setNewQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/applications/${applicationId}/qa`);
            setItems(data || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [applicationId]);

    const save = async (qid, body) => {
        const { data } = await api.patch(`/applications/${applicationId}/qa/${qid}`, body);
        setItems((arr) => arr.map((x) => (x.id === qid ? { ...x, ...data } : x)));
        return data;
    };

    const addCustom = async (e) => {
        e?.preventDefault?.();
        const q = newQ.trim();
        if (!q) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/applications/${applicationId}/qa`, { question: q });
            setItems((arr) => [...arr, data]);
            setNewQ("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const remove = async (qid) => {
        setBusy(true);
        try {
            await api.delete(`/applications/${applicationId}/qa/${qid}`);
            setItems((arr) => arr.filter((x) => x.id !== qid));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const answered = items.filter((q) => (q.answer || "").trim()).length;
    const rated = items.filter((q) => q.score).length;
    const avgScore = rated > 0
        ? Math.round(items.reduce((s, q) => s + (q.score || 0), 0) / rated * 10) / 10
        : null;

    return (
        <section data-testid="interview-qa-section">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <MessageCircleQuestion className="w-4 h-4 text-ikb" />
                    <h3 className="font-display font-bold text-sm uppercase tracking-widest">Interview Q&A</h3>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/80">
                        {answered}/{items.length} answered{avgScore !== null ? ` · Avg ${avgScore}/10` : ""}
                    </div>
                    {items.length > 0 && (
                        <QaExportPdfControls applicationId={applicationId} />
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-xs text-foreground/80 font-mono">Loading...</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border p-6 text-center text-sm text-foreground/80">
                    No interview questions yet. Add your first one below.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((q) => (
                        <QARow
                            key={q.id}
                            item={q}
                            onSave={save}
                            onDelete={remove}
                            onAddFollowup={async (text) => {
                                try {
                                    const { data } = await api.post(`/applications/${applicationId}/qa`, { question: text });
                                    setItems((arr) => [...arr, data]);
                                    toast.success("Added to your question list");
                                } catch (err) { toast.error(formatApiError(err)); }
                            }}
                            busy={busy}
                            applicationId={applicationId}
                        />
                    ))}
                </div>
            )}

            <form onSubmit={addCustom} className="mt-4 flex gap-2">
                <Input
                    value={newQ}
                    onChange={(e) => setNewQ(e.target.value)}
                    placeholder="Add your own interview question..."
                    className="rounded-none h-10"
                    data-testid="qa-new-question"
                />
                <Button type="submit" disabled={busy || !newQ.trim()} variant="outline" className="rounded-none h-10" data-testid="qa-add-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add question
                </Button>
            </form>
        </section>
    );
}
